/** Дерево «Аналитики ОПТ»: блок «Иерархия» (как в «Аналитике МП»: пресеты, перетаскивание, ↑↓,
 *  глазик), ленивое раскрытие — каждый раскрытый узел — свой запрос `GET /wholesale/tree` следующего
 *  уровня (useInfiniteQuery: «показать ещё» — следующая страница). Показатели узла — отгрузки
 *  периода (шт, выручка, маржа, маржа %; под ними Δ к периоду сравнения — кроме уровней с месяцем)
 *  и портфель заказов (к отгрузке — с датой отгрузки в периоде, просрочено — все на дату снимка;
 *  выручка, плановая маржа и %, без Δ); «*» — часть без цены / суммы (не нулём). Таблица — во всю ширину. Сортировка по
 *  столбцу — на бэкенде, по всему уровню (адрес `tsort` / `tdir`). Раскрытия — состояние компонента,
 *  сбрасываются при смене периода и уровней (ключ). Клик по листу / значок — карточка строк узла с
 *  фильтром по всему пути. Страницы уровня — одной сборки витрины (`signal`): пришла страница другой
 *  сборки — уровень перечитывается с первой страницы (без дублей и пропусков). */
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  getWholesaleTree,
  type Agg,
  type TreeDim,
  type TreeNode,
  type TreeSortKey,
} from "@/api/wholesale";
import {
  activeLevels,
  hierarchyPatch,
  hierarchyPresetPatch,
  type Hierarchy,
} from "@/lib/hierarchy";
import { cn } from "@/lib/utils";
import { monthLabel } from "../ozon-analytics/format";
import { HierarchyPanel } from "../ozon-analytics/HierarchyPanel";
import { CardButton } from "../ozon-analytics/shared";
import { thClass } from "../ozon-analytics/ui";
import {
  badLevels,
  COMMISSIONER_HINT,
  compactCell,
  isCommissioner,
  marginCell,
  nextTreeSort,
  nodeCard,
  qty,
  revenueCell,
  TREE_DIMS,
  TREE_PRESETS,
  treeSortPatch,
  type Card,
  type MoneyCell,
  type MonthRange,
  type NodeStep,
  type TreeSort,
} from "./calc";
import { treeDelta, type DeltaPart, type Measure } from "./kpi";
import { ErrorBox, MoneyTd, RED } from "./parts";
import { STICKY_HEAD, TABLE_BOX_FULL } from "./Tables";

export const TREE_PAGE = 100;
/** Минимальная ширина колонки узла; таблица — во всю ширину, остаток — узлу. */
const LABEL_MIN_CH = 40;

type Step = NodeStep;
type Group = "shipped" | "planned" | "overdue";

interface Column {
  id: string;
  group: Group;
  measure: Measure;
  /** сортировка столбца (на бэкенде); нет — заголовок без сортировки */
  sort: TreeSortKey | null;
  head: string;
  ch: number;
  title: string;
}

const COLUMNS: Column[] = [
  { id: "qty", group: "shipped", measure: "quantity", sort: "quantity", head: "Шт", ch: 9, title: "штуки отгрузок периода" },
  { id: "rev", group: "shipped", measure: "revenue", sort: "revenue", head: "Выручка", ch: 12, title: "выручка с НДС (как в 1С)" },
  { id: "mrg", group: "shipped", measure: "margin", sort: "margin", head: "Маржа", ch: 12, title: "выручка с НДС − закупка − склад, по строкам с ценой" },
  { id: "pct", group: "shipped", measure: "margin_pct", sort: "margin_pct", head: "Маржа %", ch: 9, title: "маржа / выручка строк с ценой" },
  { id: "p-rev", group: "planned", measure: "revenue", sort: "planned", head: "Выручка", ch: 12, title: "к отгрузке: остаток открытых заказов, выручка с НДС" },
  { id: "p-mrg", group: "planned", measure: "margin", sort: null, head: "Маржа", ch: 12, title: "к отгрузке: плановая маржа по строкам с ценой" },
  { id: "p-pct", group: "planned", measure: "margin_pct", sort: null, head: "%", ch: 7, title: "к отгрузке: плановая маржа / выручка строк с ценой" },
  { id: "o-rev", group: "overdue", measure: "revenue", sort: "overdue", head: "Выручка", ch: 12, title: "просрочено: остаток заказов, выручка с НДС" },
  { id: "o-mrg", group: "overdue", measure: "margin", sort: null, head: "Маржа", ch: 12, title: "просрочено: плановая маржа по строкам с ценой" },
  { id: "o-pct", group: "overdue", measure: "margin_pct", sort: null, head: "%", ch: 7, title: "просрочено: плановая маржа / выручка строк с ценой" },
];

/** Группы колонок (решение владельца 23.10.2026): к отгрузке — заказы с датой отгрузки в периоде
 *  (Σ детей = родитель), просрочено — все просроченные на дату снимка. `monthTree` — «Месяц» среди
 *  уровней: месяцы — только периода, поэтому и просроченные — с отгрузкой в месяцах периода (все —
 *  в блоке «Портфель заказов» над деревом). */
function groups(monthTree: boolean): { group: Group; head: string; title: string; tone: string }[] {
  return [
    { group: "shipped", head: "Отгружено за период", title: "отгрузки периода; под числом — Δ к периоду сравнения", tone: "" },
    {
      group: "planned",
      head: "К отгрузке — в периоде",
      title: "портфель: остаток открытых заказов на дату снимка с датой отгрузки в периоде (позже — справкой в блоке «Портфель заказов»), без Δ",
      tone: "text-sky-700 dark:text-sky-400",
    },
    {
      group: "overdue",
      head: monthTree ? "Просрочено — с отгрузкой в периоде" : "Просрочено — все",
      title: monthTree
        ? "портфель: просроченные остатки заказов на дату снимка с месяцем отгрузки в периоде (разрез «Месяц»; все просроченные — в блоке «Портфель заказов»), без Δ"
        : "портфель: ВСЕ просроченные остатки заказов на дату снимка, не только за период, без Δ",
      tone: "text-red-700 dark:text-red-400",
    },
  ];
}

const dimLabel = (code: string): string => TREE_DIMS.find((d) => d.code === code)?.label ?? code;
const stepsKey = (path: Step[]): string => JSON.stringify(path.map((p) => [p.dim, p.key]));

interface Ctx {
  range: MonthRange;
  /** период сравнения; на уровнях с месяцем (и под ним) не передаётся */
  cmp: MonthRange | null;
  /** Δ ненадёжна (сравнение вне данных) — ⚠ у Δ в ячейках */
  cmpWarning: string | null;
  levels: TreeDim[];
  sort: TreeSort;
  open: ReadonlySet<string>;
  toggle: (key: string) => void;
  onCard: (c: Card) => void;
}
const TreeCtx = createContext<Ctx | null>(null);
function useTreeCtx(): Ctx {
  const v = useContext(TreeCtx);
  if (!v) throw new Error("TreeCtx не задан");
  return v;
}

export function WholesaleTree({
  range,
  cmp,
  cmpWarning,
  hierarchy,
  treePreset,
  sort,
  onPatch,
  onCard,
}: {
  range: MonthRange;
  cmp: MonthRange | null;
  cmpWarning: string | null;
  hierarchy: Hierarchy;
  treePreset: string | null;
  sort: TreeSort;
  onPatch: (p: Record<string, string | null>) => void;
  onCard: (c: Card) => void;
}): React.ReactElement {
  const levels = activeLevels(hierarchy) as TreeDim[];
  // пустой список разрезов (`lv=,`) — та же плашка, что у чужих кодов, а не 422 бэкенда
  const bad = badLevels(hierarchy.levels);
  const empty = levels.length === 0;
  const dflt = TREE_PRESETS[0]!;
  return (
    <div className="space-y-3" data-testid="wholesale-tree">
      <HierarchyPanel
        hierarchy={hierarchy}
        dims={TREE_DIMS}
        presets={TREE_PRESETS}
        treePreset={treePreset}
        onLevels={(h) => onPatch(hierarchyPatch(h, TREE_PRESETS))}
        onPreset={(id) => onPatch(hierarchyPresetPatch(id, TREE_PRESETS))}
      />
      {bad.length > 0 || empty ? (
        <div role="alert" className={cn(RED, "flex flex-wrap items-center gap-3")} data-testid="tree-bad-levels">
          <span>
            {empty
              ? "В адресе нет ни одного разреза дерева — дерево не построено."
              : `Разрезы ${bad.map((c) => `«${c}»`).join(", ")} в адресе неизвестны или повторяются — дерево не построено.`}
          </span>
          <button type="button" className="text-xs font-medium underline" onClick={() => onPatch(hierarchyPresetPatch(dflt.id, TREE_PRESETS))}>
            Сбросить на «{dflt.label}»
          </button>
        </div>
      ) : (
        <TreeView
          key={JSON.stringify([range, levels])}
          range={range}
          cmp={cmp}
          cmpWarning={cmpWarning}
          levels={levels}
          sort={sort}
          onSort={(k) => onPatch(treeSortPatch(nextTreeSort(sort, k)))}
          onCard={onCard}
        />
      )}
      <p className="text-xs text-muted-foreground">
        Отгружено — за период{cmp ? ", под числом — Δ к периоду сравнения (кроме разреза «Месяц»)" : ""}; портфель на
        дату снимка, без Δ: к отгрузке — с датой отгрузки в периоде,{" "}
        {levels.includes("month")
          ? "просрочено — в разрезе «Месяц» только с отгрузкой в месяцах периода (все — в блоке «Портфель заказов»)"
          : "просрочено — все просроченные, не только за период"}
        . Деньги — тыс / млн, точная сумма — в подсказке. «*» — часть строк без себестоимости или суммы заказа: в маржу они не вошли (не нулём).
      </p>
    </div>
  );
}

function TreeView({
  range,
  cmp,
  cmpWarning,
  levels,
  sort,
  onSort,
  onCard,
}: {
  range: MonthRange;
  cmp: MonthRange | null;
  cmpWarning: string | null;
  levels: TreeDim[];
  sort: TreeSort;
  onSort: (k: TreeSortKey) => void;
  onCard: (c: Card) => void;
}): React.ReactElement {
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = useCallback(
    (key: string) =>
      setOpen((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );
  const ctx = useMemo<Ctx>(
    () => ({ range, cmp, cmpWarning, levels, sort, open, toggle, onCard }),
    [range, cmp, cmpWarning, levels, sort, open, toggle, onCard],
  );
  const minWidth = LABEL_MIN_CH + COLUMNS.reduce((s, c) => s + c.ch, 0);
  return (
    <TreeCtx.Provider value={ctx}>
      <div className={TABLE_BOX_FULL}>
        <table className="w-full table-fixed text-xs" style={{ minWidth: `${minWidth}ch` }} data-testid="tree-table">
          <colgroup>
            <col />
            {COLUMNS.map((c) => (
              <col key={c.id} style={{ width: `${c.ch}ch` }} />
            ))}
          </colgroup>
          <thead className={STICKY_HEAD}>
            <tr>
              <th className={cn(thClass, "text-left")} rowSpan={2} aria-sort={ariaSort(sort, "name")}>
                <SortButton sort={sort} k="name" head="Узел" title="по названию" onSort={onSort} />
              </th>
              {groups(levels.includes("month")).map((g) => (
                <th
                  key={g.group}
                  colSpan={COLUMNS.filter((c) => c.group === g.group).length}
                  className={cn(thClass, "border-l text-center normal-case", g.tone)}
                  title={g.title}
                  data-testid={`tree-group-${g.group}`}
                >
                  {g.head}
                </th>
              ))}
            </tr>
            <tr>
              {COLUMNS.map((c, i) => (
                <th
                  key={c.id}
                  className={cn(thClass, i > 0 && COLUMNS[i - 1]!.group !== c.group && "border-l")}
                  aria-sort={c.sort ? ariaSort(sort, c.sort) : undefined}
                  title={c.sort ? undefined : c.title}
                >
                  {c.sort ? (
                    <SortButton sort={sort} k={c.sort} head={c.head} title={c.title} onSort={onSort} />
                  ) : (
                    <span className="uppercase tracking-wide">{c.head}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <TreeLevel path={EMPTY} />
          </tbody>
        </table>
      </div>
    </TreeCtx.Provider>
  );
}

const EMPTY: Step[] = [];

function ariaSort(sort: TreeSort, k: TreeSortKey): "ascending" | "descending" | undefined {
  if (sort.key !== k) return undefined;
  return sort.dir === "asc" ? "ascending" : "descending";
}

function SortButton({
  sort,
  k,
  head,
  title,
  onSort,
}: {
  sort: TreeSort;
  k: TreeSortKey;
  head: string;
  title: string;
  onSort: (k: TreeSortKey) => void;
}): React.ReactElement {
  const active = sort.key === k;
  return (
    <button
      type="button"
      title={`${title}; сортировать`}
      onClick={() => onSort(k)}
      className={cn(
        "inline-flex items-center gap-1 rounded-sm uppercase tracking-wide hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        active && "text-foreground",
      )}
      data-testid={`tree-sort-${k}`}
    >
      {head}
      {active && <span data-testid="tree-sort-mark">{sort.dir === "desc" ? "↓" : "↑"}</span>}
    </button>
  );
}

const COLSPAN = 1 + COLUMNS.length;
/** Портфеля у узла нет — пусто, а не «—» (это не «нет данных», а нет открытых заказов). */
const EMPTY_OPEN: MoneyCell = { text: "", partial: false, title: "открытых заказов нет" };

/** Сравнение уровня: на уровне с месяцем и под ним не считается (месяц узла сам задаёт период). */
function levelCmp(cmp: MonthRange | null, levels: TreeDim[], depth: number): MonthRange | null {
  return cmp && !levels.slice(0, depth + 1).includes("month") ? cmp : null;
}

function TreeLevel({ path }: { path: Step[] }): React.ReactElement {
  const { range, levels, sort, cmp: pageCmp } = useTreeCtx();
  const qc = useQueryClient();
  const depth = path.length;
  const cmp = levelCmp(pageCmp, levels, depth);
  const keys = useMemo(() => path.map((p) => p.key), [path]);
  const queryKey = useMemo(
    () => ["wholesale", "tree", range.from, range.to, levels, keys, sort, cmp?.from ?? null, cmp?.to ?? null],
    [range.from, range.to, levels, keys, sort, cmp],
  );
  const q = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) =>
      getWholesaleTree(
        {
          month_from: range.from,
          month_to: range.to,
          levels,
          path: keys,
          sort: sort.key,
          dir: sort.dir,
          limit: TREE_PAGE,
          offset: pageParam,
          cmp,
        },
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((s, p) => s + p.nodes.length, 0);
      return loaded < last.total_count ? loaded : undefined;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const pages = q.data?.pages ?? [];
  // страницы разных сборок не склеиваем: сброс и чтение уровня с первой страницы
  const mixed = pages.some((p) => p.signal !== pages[0]!.signal);
  useEffect(() => {
    if (mixed) void qc.resetQueries({ queryKey, exact: true });
  }, [mixed, qc, queryKey]);
  const indent = { paddingLeft: `${12 + depth * 18}px` };
  if (mixed) {
    return (
      <tr>
        <td colSpan={COLSPAN} className="py-2 text-xs text-muted-foreground" style={indent} data-testid="tree-rebuilt">
          Витрина пересобрана — перечитываю уровень с начала…
        </td>
      </tr>
    );
  }
  if (q.isLoading) {
    return (
      <tr>
        <td colSpan={COLSPAN} className="py-2 text-xs text-muted-foreground" style={indent}>
          Загружаю…
        </td>
      </tr>
    );
  }
  if (q.isError && !q.data) {
    return (
      <tr>
        <td colSpan={COLSPAN} className="py-2 pr-2" style={indent}>
          <ErrorBox error={q.error} what="уровень дерева" />
        </td>
      </tr>
    );
  }
  const dim = pages[0]?.dim ?? levels[depth]!;
  // фоновое обновление упало, а данные есть — показываем ошибку и помечаем строки устаревшими
  const stale = q.isRefetchError;
  const nodes = pages.flatMap((p) => p.nodes);
  const total = pages[0]?.total_count ?? 0;
  if (nodes.length === 0) {
    return (
      <tr>
        <td colSpan={COLSPAN} className="py-2 text-xs text-muted-foreground" style={indent} data-testid="tree-empty">
          Отгрузок в периоде нет.
        </td>
      </tr>
    );
  }
  return (
    <>
      {stale && (
        <tr>
          <td colSpan={COLSPAN} className="py-2 pr-2" style={indent} data-testid="tree-stale">
            <ErrorBox error={q.error} what="обновление уровня дерева — строки ниже показаны по прежней загрузке, данные могли устареть" />
          </td>
        </tr>
      )}
      {nodes.map((n) => (
        <TreeNodeRows key={JSON.stringify(n.key)} node={n} dim={dim} path={path} stale={stale} hasCmp={cmp !== null} />
      ))}
      {q.hasNextPage && (
        <tr>
          <td colSpan={COLSPAN} className="py-1" style={{ paddingLeft: `${30 + depth * 18}px` }}>
            <button
              type="button"
              className="text-xs text-primary hover:underline disabled:opacity-50"
              disabled={q.isFetchingNextPage}
              onClick={() => void q.fetchNextPage()}
              data-testid="tree-more"
            >
              {q.isFetchingNextPage
                ? "Загружаю…"
                : `Показать ещё ${Math.min(TREE_PAGE, total - nodes.length)} из ${total - nodes.length}`}
            </button>
          </td>
        </tr>
      )}
      {q.isFetchNextPageError && (
        <tr>
          <td colSpan={COLSPAN} className="py-2 pr-2" style={indent}>
            <ErrorBox error={q.error} what="следующую страницу" />
          </td>
        </tr>
      )}
    </>
  );
}

function TreeNodeRows({
  node,
  dim,
  path,
  stale,
  hasCmp,
}: {
  node: TreeNode;
  dim: TreeDim;
  path: Step[];
  stale: boolean;
  hasCmp: boolean;
}): React.ReactElement {
  const { open, toggle, onCard } = useTreeCtx();
  const step = useMemo<Step>(() => ({ dim, key: node.key, label: node.label }), [dim, node.key, node.label]);
  const childPath = useMemo(() => [...path, step], [path, step]);
  const key = stepsKey(childPath);
  const isOpen = node.has_children && open.has(key);
  return (
    <>
      <NodeRow
        node={node}
        dim={dim}
        depth={path.length}
        open={isOpen}
        stale={stale}
        hasCmp={hasCmp}
        onToggle={node.has_children ? () => toggle(key) : undefined}
        onCard={() => onCard(nodeCard(path, step))}
      />
      {isOpen && <TreeLevel path={childPath} />}
    </>
  );
}

const CHIP = "ml-1.5 inline-block rounded bg-amber-100 px-1 text-[10px] font-normal text-amber-800 dark:bg-amber-950 dark:text-amber-300";
/** Нейтральная пометка (не проблема): покупатель — комиссионер (v1.4). */
const NOTE_CHIP = "ml-1.5 inline-block rounded bg-muted px-1 text-[10px] font-normal text-muted-foreground";

/** Строк узла с проблемным статусом цены — по отгрузкам периода и портфелю. */
function statusRows(n: TreeNode, status: "no_price" | "no_warehouse_rate"): number {
  return [n.shipped, n.planned, n.overdue].reduce((s, a) => s + (a ? a.price_status[status].rows : 0), 0);
}

const NO_SHIPPED: MoneyCell = { text: "—", partial: false, title: "отгрузок в периоде нет" };

/** Ячейка показателя: компактные деньги / штуки / %, «—» с причиной. */
function measureCell(a: Agg | null, measure: Measure): MoneyCell {
  if (!a) return NO_SHIPPED;
  switch (measure) {
    case "quantity":
      return { text: qty(a.quantity), partial: false, title: null };
    case "revenue":
      return compactCell(revenueCell(a), a.revenue_gross);
    case "margin":
      return compactCell(marginCell(a), a.net_margin);
    case "margin_pct": {
      const m = marginCell(a);
      return { text: m.pct, partial: false, title: m.title };
    }
  }
}

function NodeRow({
  node,
  dim,
  depth,
  open,
  stale,
  hasCmp,
  onToggle,
  onCard,
}: {
  node: TreeNode;
  dim: TreeDim;
  depth: number;
  open: boolean;
  stale: boolean;
  hasCmp: boolean;
  onToggle?: () => void;
  onCard: () => void;
}): React.ReactElement {
  const { cmpWarning, levels } = useTreeCtx();
  // узел только из сравнения, ниже — разрез без сравнения (месяц): раскрывать нечего — подсказка
  const cmpOnly = !node.shipped && !node.planned && !node.overdue && node.shipped_cmp !== null;
  const noKids = cmpOnly && !node.has_children && depth + 1 < levels.length;
  const noPrice = statusRows(node, "no_price");
  const noRate = statusRows(node, "no_warehouse_rate");
  const unresolved = node.shipped?.base_unresolved.rows ?? 0;
  const label = dim === "month" && node.key ? monthLabel(node.key) : node.label;
  // строка: узел — раскрыть/свернуть; лист — карточка строк
  const onRow = onToggle ?? onCard;
  return (
    <tr
      className={cn("cursor-pointer border-t hover:bg-accent/30", stale && "opacity-60")}
      data-stale={stale || undefined}
      onClick={onRow}
      aria-expanded={onToggle ? open : undefined}
      data-testid="tree-row"
      data-dim={dim}
      data-depth={depth}
      title={
        noKids
          ? "узел только из периода сравнения; ниже — разрез «Месяц», где сравнение не считается: раскрывать нечего"
          : undefined
      }
    >
      <td className="py-1 pr-2" style={{ paddingLeft: `${8 + depth * 18}px` }}>
        <div className="flex items-start gap-1">
          <span className="mt-0.5 w-4 shrink-0 text-muted-foreground">
            {onToggle && (open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />)}
          </span>
          <div className="min-w-0">
            <div className="flex items-start gap-1">
              <div className="min-w-0">
                <span className="mr-1.5 text-[10.5px] uppercase tracking-wide text-muted-foreground">{dimLabel(dim)}</span>
                <span className="font-medium" data-testid="tree-label">
                  {label}
                </span>
                {noPrice > 0 && (
                  <span className={CHIP} data-testid="tree-chip" title="строки без себестоимости: маржи по ним нет">
                    нет цены: {noPrice} стр.
                  </span>
                )}
                {noRate > 0 && (
                  <span className={CHIP} data-testid="tree-chip" title="строки без ставки склада: маржи по ним нет">
                    нет ставки склада: {noRate} стр.
                  </span>
                )}
                {unresolved > 0 && (
                  <span className={CHIP} data-testid="tree-chip">
                    корр. без накладной
                  </span>
                )}
                {dim === "buyer" && isCommissioner(node) && (
                  <span className={NOTE_CHIP} data-testid="tree-commissioner" title={COMMISSIONER_HINT}>
                    комиссионер
                  </span>
                )}
              </div>
              <CardButton label={`Карточка: ${label}`} title="Строки (карточка)" onOpen={onCard} />
            </div>
            {node.sublabel && (
              <div className="truncate text-xs text-muted-foreground" title={node.sublabel}>
                {node.sublabel}
              </div>
            )}
          </div>
        </div>
      </td>
      {COLUMNS.map((c, i) => {
        const a = node[c.group];
        const delta: DeltaPart | null | undefined =
          c.group === "shipped" && hasCmp ? treeDelta(node.shipped, node.shipped_cmp, c.measure) : undefined;
        return (
          <MoneyTd
            key={c.id}
            cell={a || c.group === "shipped" ? measureCell(a, c.measure) : EMPTY_OPEN}
            delta={delta}
            deltaWarning={cmpWarning}
            className={cn(i > 0 && COLUMNS[i - 1]!.group !== c.group && "border-l")}
            testId={`tree-${c.id}`}
          />
        );
      })}
    </tr>
  );
}
